import moment from "moment-timezone";
import { env } from "../appConfig";
import { store } from "../appStore/store";
import getApiResponse from "./getApiResponse";
import { buildRelayUrl } from "./relayService";

export const NOTE_MAX_LENGTH = 1000;
export const NOTE_EDIT_WINDOW_MS = 2 * 60 * 1000;
const CLOCK_SKEW_MS = 5000;

const COMMENTS_BASE = `${env.REACT_APP_API_CORE_BASE_URL}/booking/b2b/comments`;
const COMMENTS_CONTEXT = "MyFiles";
const HAS_TZ_OFFSET = /Z$|[+-]\d{2}:\d{2}$/;

const parseCommentTimestamp = (value) => {
  if (!value) {
    return null;
  }
  const raw = String(value).trim();
  const parsed = HAS_TZ_OFFSET.test(raw)
    ? moment.parseZone(raw)
    : moment.utc(raw);
  return parsed.isValid() ? parsed.toISOString() : null;
};

export const formatCommentTimestamp = (value) => {
  if (!value) {
    return "";
  }
  const parsed = moment.utc(value);
  return parsed.isValid() ? parsed.format("D MMM YYYY, HH:mm") : "";
};

export const isNoteEditable = (note, currentUserId, now = Date.now()) => {
  if (!note || !currentUserId || note.createdBy !== currentUserId) {
    return false;
  }
  const createdAt = new Date(note.createdAt).getTime();
  if (Number.isNaN(createdAt)) {
    return false;
  }
  const ageMs = now - createdAt;
  if (ageMs < -CLOCK_SKEW_MS) {
    return false;
  }
  return ageMs < NOTE_EDIT_WINDOW_MS;
};

const validateText = (text) => {
  const trimmed = (text || "").trim();
  if (!trimmed) {
    throw new Error("Note text is required.");
  }
  if (trimmed.length > NOTE_MAX_LENGTH) {
    throw new Error(`Note cannot exceed ${NOTE_MAX_LENGTH} characters.`);
  }
  return trimmed;
};

const getRequestHeaders = () => {
  let corrId;
  try {
    corrId = store.getState()?.searchForm?.uuid;
  } catch (err) {
    corrId = undefined;
  }
  return {
    "Content-Type": "application/json",
    "Access-Control-Allow-Origin": "*",
    "Access-Control-Allow-Credentials": true,
    "cache-control": "no-cache",
    bookingChannel: "internal",
    ...(corrId ? { correlationId: corrId } : {}),
  };
};

const assertApiSuccess = (response, fallbackMessage) => {
  if (response == null) {
    throw new Error(fallbackMessage);
  }
  if (response === "error") {
    throw new Error(fallbackMessage);
  }
  if (response?.error) {
    const nested =
      typeof response.error === "object"
        ? response.error.message
        : response.error;
    throw new Error(response.message || nested || fallbackMessage);
  }
  return response;
};

const normalizeComment = (raw) => {
  if (!raw) {
    return null;
  }
  return {
    id: raw.id,
    pnr: raw.pnr,
    text: raw.comments ?? "",
    createdBy: raw.loggedUser ?? "",
    createdAt: parseCommentTimestamp(raw.creationTimestamp),
    updatedAt:
      parseCommentTimestamp(raw.updationTimestamp) ||
      parseCommentTimestamp(raw.creationTimestamp),
  };
};

const sortChronological = (notes = []) =>
  [...notes].sort((a, b) => {
    const aTime = new Date(a.createdAt || 0).getTime();
    const bTime = new Date(b.createdAt || 0).getTime();
    const aValid = !Number.isNaN(aTime) ? aTime : 0;
    const bValid = !Number.isNaN(bTime) ? bTime : 0;
    if (aValid !== bValid) {
      return aValid - bValid;
    }
    return Number(a.id || 0) - Number(b.id || 0);
  });

const postComments = async (path, data, fallbackMessage) => {
  const response = await getApiResponse({
    method: "POST",
    url: buildRelayUrl(`${COMMENTS_BASE}${path}`),
    headers: getRequestHeaders(),
    data,
  });
  return assertApiSuccess(response, fallbackMessage);
};

export const getNotes = async (pnr) => {
  if (!pnr) {
    return [];
  }
  const response = await postComments(
    "/get",
    {
      pnr,
      context: "",
      loggedUser: "",
      comment: "",
    },
    "Unable to load notes."
  );
  const list = Array.isArray(response?.comments) ? response.comments : [];
  return sortChronological(list.map(normalizeComment).filter(Boolean));
};

export const createNote = async ({ pnr, text, createdBy }) => {
  const trimmed = validateText(text);
  if (!pnr || !createdBy) {
    throw new Error("Unable to add note.");
  }
  const response = await postComments(
    "/add",
    {
      pnr,
      comments: trimmed,
      context: COMMENTS_CONTEXT,
      loggedUser: createdBy,
    },
    "Unable to add note."
  );
  if (response.success === false || !response.comment) {
    throw new Error(response.message || "Unable to add note.");
  }
  return normalizeComment(response.comment);
};

export const updateNote = async ({ id, text, createdBy, createdAt }) => {
  const trimmed = validateText(text);
  const numericId = Number(id);
  if (!numericId || numericId <= 0) {
    throw new Error("Unable to update note.");
  }
  if (
    !isNoteEditable(
      { createdBy, createdAt },
      createdBy
    )
  ) {
    throw new Error("This note can no longer be edited.");
  }
  const response = await postComments(
    "/update",
    {
      id: numericId,
      comments: trimmed,
    },
    "Unable to update note."
  );
  if (response.success === false || !response.comment) {
    throw new Error(response.message || "Unable to update note.");
  }
  return normalizeComment(response.comment);
};

export const deleteNote = async ({ id, createdBy, createdAt }) => {
  const numericId = Number(id);
  if (!numericId || numericId <= 0) {
    throw new Error("Unable to delete note.");
  }
  if (
    !isNoteEditable(
      { createdBy, createdAt },
      createdBy
    )
  ) {
    throw new Error("This note can no longer be deleted.");
  }
  const response = await postComments(
    "/delete",
    { id: numericId },
    "Unable to delete note."
  );
  if (response.success === false || !(response.deletedCount > 0)) {
    throw new Error(
      response.message || "No matching comments found to delete"
    );
  }
  return { id: numericId };
};

const bookingNotesService = {
  getNotes,
  createNote,
  updateNote,
  deleteNote,
};

export default bookingNotesService;
